import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BasicDialog, Colors } from 'library';
import { client } from 'shared';
import { Business, BusinessLocation, BusinessLocationPreferredWorker, ShiftProviderUser } from 'models';
import { useUserContext } from 'UserContext';

import Error from '../../components/Error';
import { TextContainer } from '../../components/Containers';
import { Header, InnerModalContent, PreferredWorkerModalContext } from './components';

export default function SetPreferredModal({
    open,
    onSuccess,
    onClose,
    provider,
    businessLocation,
}: {
    open: boolean;
    onSuccess: (preferredWorkers: BusinessLocationPreferredWorker[]) => void;
    onClose: () => void;
    provider: ShiftProviderUser;
    businessLocation?: BusinessLocation;
}) {
    const { user } = useUserContext();
    const [business, setBusiness] = useState<Business | undefined>(user?.businesses_active?.[0]);
    const [locations, setLocations] = useState<BusinessLocationPreferredWorker[]>([]);
    const [empty, setEmpty] = useState(false);
    const [error, setError] = useState(false);

    const businessIdNumber: number | undefined = business?.id ?? businessLocation?.business ?? undefined;

    useEffect(() => {
        if (open) {
            if (businessIdNumber && provider.id) {
                client(
                    `api/preferred-worker/locations-and-statuses?business_id=${businessIdNumber}&preferred_worker_id=${provider.id}`,
                    {
                        method: 'GET',
                    },
                )
                    .then((response: BusinessLocationPreferredWorker[]) => {
                        setLocations(response);

                        response?.length === 0 ? setEmpty(true) : setBusiness(response[0].business);
                    })
                    .catch(() => setError(true));
            } else {
                setError(true);
            }
        }
    }, [businessIdNumber, provider.id, open]);

    return (
        <BasicDialog
            isOpen={open}
            onClose={() => {
                setError(false);
                onClose();
            }}
            dialogTitle={error ? '' : `Add ${provider.first_name} to your preferred workers list`}
            closeButton
        >
            {!user || !businessIdNumber || error ? (
                <>
                    <Error />
                    <TextContainer>
                        <Link
                            to="/home"
                            style={{ color: Colors.darkNavy, textDecoration: 'underline', fontWeight: 'bold' }}
                        >
                            Back home
                        </Link>
                    </TextContainer>
                </>
            ) : (
                <PreferredWorkerModalContext.Provider
                    value={{
                        isAdmin: user?.admin ?? false,
                        business: business ?? {
                            id: businessIdNumber,
                            name: businessLocation?.name ?? `Business (${businessIdNumber})`,
                        },
                        provider,
                        emptyLocationList: empty,
                        locations,
                        businessLocation,
                        setLocations,
                        setError,
                        onSuccess,
                        onClose,
                    }}
                >
                    <Header />
                    <InnerModalContent />
                </PreferredWorkerModalContext.Provider>
            )}
        </BasicDialog>
    );
}
